// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Domain jargon, defined for a reader who knows nothing about carbon policy. */
export interface GlossaryEntry {
  term: string;
  title: string;
  body: string;
}

export const GLOSSARY: Record<string, GlossaryEntry> = {
  safeguard: {
    term: 'safeguard',
    title: 'The Safeguard Mechanism',
    body:
      'Australia’s main policy for cutting industrial greenhouse gases. It covers the country’s largest emitting facilities — any that release more than 100,000 tonnes of CO₂-equivalent a year — and gives each one a legally-binding emissions "baseline" that falls a little every year, on a path to net zero by 2050. A facility that stays under its baseline earns credits; one that goes over must make up the difference by surrendering carbon credits.',
  },
  covered: {
    term: 'covered',
    title: 'Covered emissions',
    body:
      'The greenhouse gases a facility releases directly (its "scope 1" emissions) that fall under the Safeguard Mechanism, measured in tonnes of carbon-dioxide equivalent. It does not include the emissions from later burning the coal or gas a mine or well produces — only what is released at the facility itself.',
  },
  baseline: {
    term: 'baseline',
    title: 'Baseline',
    body:
      'The emissions limit set for each facility. Under the reformed scheme (from July 2023) most baselines decline by about 4.9% every year, tightening the cap over time. A facility whose covered emissions come in below its baseline has beaten its limit; above it has exceeded it.',
  },
  co2e: {
    term: 'co2e',
    title: 'CO₂-equivalent (tCO₂-e)',
    body:
      'A common unit that lets different greenhouse gases be added together. One tonne of methane traps far more heat than one tonne of carbon dioxide, so it is converted to the number of tonnes of CO₂ that would have the same warming effect. Everything on this site is in tonnes of CO₂-equivalent.',
  },
  excess: {
    term: 'excess',
    title: 'Over / under the baseline',
    body:
      'This site’s headline measure is a facility’s covered emissions minus its baseline. A positive number means it emitted more than its cap this year (over the line); a negative number means it came in under. This is reported for every facility, whether or not it has formally settled a net position.',
  },
  netposition: {
    term: 'netposition',
    title: 'Net position',
    body:
      'A facility’s covered emissions after subtracting any carbon credits it surrendered, compared with its baseline. A net position at or below zero means the facility is compliant. Not every facility settles a net position each year — some defer under a multi-year monitoring period — so this is shown only where published.',
  },
  accu: {
    term: 'accu',
    title: 'ACCU — Australian Carbon Credit Unit',
    body:
      'A tradeable credit representing one tonne of CO₂-e that has been avoided or stored somewhere else in the economy (for example by regenerating vegetation or capturing landfill gas). A Safeguard facility that emits above its baseline can buy and surrender ACCUs to cover the gap instead of cutting its own emissions.',
  },
  smc: {
    term: 'smc',
    title: 'SMC — Safeguard Mechanism Credit',
    body:
      'A credit issued to a Safeguard facility that emits below its baseline, one per tonne of CO₂-e it came in under. The facility can sell these to other Safeguard facilities that are over their baseline, or bank them — creating a market in emissions inside the scheme.',
  },
  methane: {
    term: 'methane',
    title: 'Methane (CH₄)',
    body:
      'A powerful greenhouse gas released by coal mines and gas operations (as "fugitive" leaks) and by landfills. Because it traps far more heat than carbon dioxide, even modest tonnages matter. It is reported here already converted to CO₂-equivalent, so it can be compared directly with a facility’s carbon-dioxide emissions.',
  },
  fugitive: {
    term: 'fugitive',
    title: 'Fugitive emissions',
    body:
      'Greenhouse gases that escape unintentionally rather than through a smokestack — methane venting from a coal seam as it is mined, or leaking from gas wells, pipelines and processing. They are the reason coal mining and gas extraction show such a high share of methane in their emissions.',
  },
  anzsic: {
    term: 'anzsic',
    title: 'Industry (ANZSIC)',
    body:
      'Each facility is classified by the Australian and New Zealand Standard Industrial Classification — the official coding of what an operation does (coal mining, oil and gas extraction, cement manufacturing, and so on). This site groups facilities by that classification to compare industries.',
  },
  responsible: {
    term: 'responsible',
    title: 'Responsible emitter',
    body:
      'The company legally accountable for a facility’s Safeguard obligations — usually the operator or its parent. One company can be the responsible emitter for many facilities, which is why this site rolls facilities up to a parent-company view.',
  },
  scope1: {
    term: 'scope1',
    title: 'Scope 1 (direct) emissions',
    body:
      'Emissions released directly by a facility’s own activities — burning fuel on site, chemical reactions, methane venting. The Safeguard Mechanism covers scope 1 only. Emissions from the electricity a facility buys (scope 2), or from customers later using its products (scope 3), are not counted here.',
  },
};

export function glossaryTerm(term: string, label?: string): string {
  const entry = GLOSSARY[term];
  const text = label ?? entry?.title ?? term;
  return `<span class="glossary-link" data-term="${term}" tabindex="0" role="button">${text}</span>`;
}

export function infoIcon(term: string): string {
  return `<button class="info-icon" data-term="${term}" aria-label="What does this mean?" type="button">i</button>`;
}
